import { InferType, array, boolean, mixed, number, object, string } from "yup";
import { AppDispatch, RootState } from "../../config/store";
import { toast } from "react-toastify";
import {
  CUSTOM_KEY_TYPES,
  DEFAULT_CREATE_BOOKING_FORM,
  OUTLETS,
  setAppointments,
  setAvailableSlots,
  setCalendars,
  setCreateBookingForm,
  setCustomFields,
  setCustomKeys,
  setIsLoadingSlots,
  typeAvailableSlot,
  typeCustomKeyTypes,
  typeOutlets,
} from "./HomeSlice";
import _axios, { VERSION } from "../../config/axios";
import { setConfirmation, setError } from "../Confirmation/ConfirmationSlice";
import { setIsLoading } from "../../AppSlice";
import { findLastIndex } from "../../config/helper";
import axios from "axios";
import moment from "moment";

const calendarSchema = object({
  uuid: string().required(),
  interval: number().required().min(1),
  color: string()
    .required()
    .matches(/^#(?:[0-9a-fA-F]{3}){1,2}$/),
  name: string().required(),
  order: number().required().min(0),
  openHours: array(
    object({
      uuid: string().required(),
      day: number().required(),
      endTime: string().required(),
      startTime: string().required(),
    })
  ).required(),
  customFields: array(
    object({
      uuid: string().required(),
      value: string(),
      customKey: object({
        uuid: string().required(),
        name: string().required(),
      }).required(),
    }).required()
  ).required(),
}).required();

const getCalendarsResponseSchema = object({
  data: object({
    calendars: array(calendarSchema).required(),
  }),
});

export type typeGetCalendarsResponseSchema = InferType<
  typeof getCalendarsResponseSchema
>;

export type typeCalendar = InferType<typeof calendarSchema>;

export const getCalendars =
  ({ outlet }: { outlet: typeOutlets }) =>
  (dispatch: AppDispatch) => {
    return _axios({ outlet })
      .get<typeGetCalendarsResponseSchema>("calendars")
      .then(async (response) => {
        const validResponse = await getCalendarsResponseSchema.validate(
          response
        );
        dispatch(setCalendars(validResponse.data.calendars));
      })
      .catch((error) => {
        toast.error(error.response?.data?.message ?? error.message);
        console.error(error);
      });
  };

const customFieldSchema = object({
  uuid: string(),
  value: string(),
  customKey: object({
    name: string(),
    uuid: string(),
  }),
});

const calendarNewSchema = object({
  uuid: string(),
  name: string(),
  organisation: object({
    name: string(),
    uuid: string(),
  }),
  customFields: array(customFieldSchema),
});

const appointmentSchema = object({
  uuid: string().required(),
  startTime: string().required(),
  endTime: string().required(),
  calendar: object({
    name: string().required(),
    uuid: string().required(),
  }).required(),
}).required();

export type typeAppointmentSchema = InferType<typeof appointmentSchema>;

const getAppointmentsResponseSchema = object({
  data: object({
    appointments: array(appointmentSchema).required(),
  }).required(),
});

export type typeGetAppointmentsResponseSchema = InferType<
  typeof getAppointmentsResponseSchema
>;

export const getAppointmentsWithinADay =
  ({
    outlet,
    startTime,
    endTime,
    calendarUuids,
  }: {
    outlet: typeOutlets;
    startTime: {
      startBound: string;
      endBound: string;
    };
    endTime: {
      startBound: string;
      endBound: string;
    };
    calendarUuids: string[];
  }) =>
  (dispatch: AppDispatch) => {
    return _axios({ outlet })
      .post<typeGetAppointmentsResponseSchema>("/appointment/within-a-day", {
        startTime,
        endTime,
        calendarUuids,
      })
      .then(async (response) => {
        const validResponse = await getAppointmentsResponseSchema.validate(
          response
        );
        dispatch(setAppointments(validResponse.data.appointments));
      })
      .catch((error) => {
        toast.error(error.response?.data?.message ?? error.message);
        console.error(error);
      });
  };

const getAvailableSlotsResponseSchema = object({
  data: object({
    calendars: array(
      object({
        uuid: string().required(),
        name: string().required(),
        slots: array(
          object({
            startTime: string().required(),
            endTime: string().required(),
          }).required()
        ).required(),
      })
    ).required(),
  }),
});

export const fetchAvailableSlots =
  ({
    outlet,
    date,
    calendarUuids,
  }: {
    outlet: (typeof OUTLETS)[number];
    date: Date;
    calendarUuids: string[];
  }) =>
  async (dispatch: AppDispatch) => {
    try {
      const todaySlots = await getAvailableSlots({
        outlet,
        date,
        calendarUuids,
      });

      const nextDay = new Date(date);
      nextDay.setDate(date.getDate() + 1);
      const nextDaySlots = await getAvailableSlots({
        outlet: outlet,
        date: nextDay,
        calendarUuids: calendarUuids,
      });

      const availableSlots = [...todaySlots, ...nextDaySlots];
      const finalOutput = mergeSlots(availableSlots).filter(({ startTime }) => {
        return new Date(startTime).getDate() === new Date(date).getDate();
      });
      dispatch(setIsLoadingSlots(false));
      dispatch(setAvailableSlots(finalOutput));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

/**
 * Merge slots if time difference <= 5 minutes.
 * @param slots Array of available slots
 * @returns An array with merged slots
 */
export const mergeSlots = (_slots: typeAvailableSlot[]) => {
  const output = [] as typeAvailableSlot[];
  const slots = [..._slots].sort(
    (a, b) => new Date(a.startTime)?.getTime() - new Date(b.startTime).getTime()
  );
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const previousIndex = findLastIndex(
      output,
      (prevSlot: (typeof output)[number]) =>
        prevSlot.calendarUuid === slot.calendarUuid
    );
    output.push(slot);
    if (previousIndex === -1) continue;
    const previous = output[previousIndex];
    const start = moment(new Date(slot.startTime));
    const end = moment(new Date(previous.endTime));
    if (Math.abs(end.diff(start, "minutes")) <= 5) {
      previous.endTime = slot.endTime;
      output[previousIndex] = previous;
    }
  }
  return output;
};

/**
 * Retrieve available slots for a single date.
 * @param outlet HaveFun outlet name
 * @param date date to retrieve slot
 * @param calendarUuids array of calendar uuids
 * @returns An array with merged slots
 */
export const getAvailableSlots = ({
  outlet,
  date,
  calendarUuids,
}: {
  outlet: typeOutlets;
  date: Date;
  calendarUuids: string[];
}) => {
  return _axios({ outlet })
    .post<typeGetCalendarsResponseSchema>("calendars/available-slots", {
      date: date.toDateString(),
      calendarUuids: calendarUuids,
    })
    .then(async (response) => {
      const validResponse = await getAvailableSlotsResponseSchema.validate(
        response
      );

      const mappedResponse = validResponse.data.calendars
        .map((calendar) =>
          calendar.slots.map((slot) => ({
            calendarUuid: calendar.uuid,
            calendarName: calendar.name,
            startTime: slot.startTime,
            endTime: slot.endTime,
          }))
        )
        .flat();
      return mappedResponse ?? [];
    })
    .catch((error) => {
      toast.error(error.response?.data?.message ?? error.message);
      console.error(error);
      return [];
    });
};

export const createAppointmentRequestSchema = object({
  title: string().required().min(1),
  color: string()
    .matches(/^#(?:[0-9a-fA-F]{3}){1,2}$/)
    .required(),
  startTime: string().required(),
  endTime: string().required(),
  countryCode: string(),
  nationalNumber: string(),
  phone: string(),
  name: string(),
  calendarUuid: string().required(),
  customFields: array(
    object({
      customKeyUuid: string().required().min(1),
      value: string(),
    }).required()
  ).required(),
}).required();

export type typeCreateAppointmentRequestSchema = InferType<
  typeof createAppointmentRequestSchema
>;

const createAppointmentResponseSchema = object({
  data: object({
    message: string().required(),
    appointment: object({
      uuid: string().required(),
      startTime: string().required(),
      endTime: string().required(),
    }),
  }).required(),
});

export const createAppointment =
  ({
    outlet,
    createAppointmentForm,
  }: {
    outlet: (typeof OUTLETS)[number];
    createAppointmentForm: typeCreateAppointmentRequestSchema;
  }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    return _axios({ outlet })
      .post<Request, Response>("appointment/new", {
        ...createAppointmentForm,
        sendMessage: true,
      })
      .then(async (response) => {
        const validResponse = await createAppointmentResponseSchema.validate(
          response
        );
        const appointment = validResponse.data.appointment;

        dispatch(
          setConfirmation({
            appointmentUuid: appointment.uuid,
            startTime: appointment.startTime,
            endTime: appointment.endTime,
            outlet: getState().home.createBookingForm.outlet,
            roomType: getState().home.createBookingForm.roomType as string,
            name: getState().home.createBookingForm.name,
            phone: getState().home.createBookingForm.phone,
            duration: getState().home.createBookingForm.duration,
          })
        );
        dispatch(setCreateBookingForm(DEFAULT_CREATE_BOOKING_FORM));
        dispatch(setIsLoading(false));
      })
      .catch((error) => {
        console.log(error);
        const message =
          error.response?.status === 409
            ? "The timeslot you've chosen is no longer available. Please choose another timeslot."
            : "Unable to schedule appointment, please try again";
        dispatch(
          setError({
            isError: true,
            message: message,
          })
        );
        dispatch(setIsLoading(false));
        toast.error(error.response?.data?.message ?? error.message);
      });
  };

const postCustomKeyFilteredIndexRequestSchema = object({
  attributes: object({
    include: array(string().required()).required(),
    exclude: array(string().required()).required(),
  }),
  filter: array(
    object({
      field: string().required(),
      operator: mixed<
        | "contains"
        | "equals"
        | "startsWith"
        | "endsWith"
        | "isEmpty"
        | "isNotEmpty"
        | "isAnyOf"
      >()
        .oneOf([
          "contains",
          "equals",
          "startsWith",
          "endsWith",
          "isEmpty",
          "isNotEmpty",
          "isAnyOf",
        ])
        .required(),
      value: mixed<Array<string> | string>().when("operator", (operator) =>
        operator[0] === "isAnyOf" ? array(string()) : string()
      ),
    })
  ).required(),
});

export type typePostCustomKeysFilteredIndexRequestSchema = InferType<
  typeof postCustomKeyFilteredIndexRequestSchema
>;

const customKeySchema = object({
  uuid: string().required(),
  name: string().required(),
  isRequired: boolean().required(),
  type: mixed<typeCustomKeyTypes>().oneOf(CUSTOM_KEY_TYPES).required(),
  targetClass: string(),
  metadata: object(),
}).required();

const postCustomKeysFilteredIndexResponseSchema = object({
  data: object({
    customKeys: array(customKeySchema).required(),
  }),
});

export type typePostCustomKeysFilteredIndexResponseSchema = InferType<
  typeof postCustomKeysFilteredIndexResponseSchema
>;

export type typeCustomKey = Omit<
  InferType<typeof customKeySchema>,
  "metadata"
> & { metadata: { [x: string]: any } };

export const getCustomKeys =
  ({ outlet }: { outlet: (typeof OUTLETS)[number] }) =>
  (dispatch: AppDispatch) => {
    return _axios({ outlet })
      .post<
        typePostCustomKeysFilteredIndexRequestSchema,
        typePostCustomKeysFilteredIndexResponseSchema
      >("custom-keys/filtered", {
        attributes: {
          include: [
            "uuid",
            "name",
            "isRequired",
            "type",
            "metadata",
            "targetClass",
          ],
          exclude: [],
        },
        filter: [{ field: "name", operator: "isNotEmpty", value: "" }],
      })
      .then(async (response) => {
        const validResponse =
          await postCustomKeysFilteredIndexResponseSchema.validate(response);
        const filteredCustomkey = validResponse.data.customKeys.filter(
          ({ targetClass }) =>
            ["appointment", "calendar"].includes(
              targetClass?.toLocaleLowerCase().trim() ?? ""
            )
        );
        dispatch(setCustomKeys(filteredCustomkey));
      })
      .catch((error) => {
        toast.error(error.response?.data?.message ?? error.message);
        console.error(error);
      });
  };

const organisationSchema = object({
  uuid: string().required(),
  logo: string().nullable(),
  createdAt: string().required(),
  name: string().required(),
  customFields: array(
    object({
      uuid: string().required(),
      value: string(),
      customKey: object({
        uuid: string().required(),
        name: string().required(),
      }).required(),
    }).required()
  ).required(),
}).required();

const getOrganisationResponseSchema = object({
  message: string().required(),
  organisation: organisationSchema,
}).required();

type typeGetOrganisationResponseSchema = InferType<
  typeof getOrganisationResponseSchema
>;
export type typeOrganisation =
  typeGetOrganisationResponseSchema["organisation"];

export type typeCustomFields =
  typeGetOrganisationResponseSchema["organisation"]["customFields"];

export const getOrganisation =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const response = await axios.get<typeGetOrganisationResponseSchema>(
        `${process.env.REACT_APP_EXABLOOM_SERVICE_URL}/organisation`,
        {
          headers: {
            version: process.env.REACT_APP_EXABLOOM_SERVICE_VERSION ?? VERSION,
            organisationuuid:
              process.env.REACT_APP_HAVEFUN_ONLINE_BOOKING_ORGANISAITON_UUID,
            [`${process.env.REACT_APP_EXABLOOM_KEY}`]:
              process.env.REACT_APP_HAVEFUN_ONLINE_BOOKING_SECRET ?? "",
          },
        }
      );
      const validResponse = await getOrganisationResponseSchema.validate(
        response.data
      );
      dispatch(setCustomFields(validResponse.organisation.customFields));
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? error.message);
      console.error(error);
      return null;
    }
  };
